import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Card, Group, MultiSelect, NumberInput, Stack } from '@mantine/core'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import { useBranchQuery, useBranchVehicleClassesQuery, useUpdateBranchMutation } from '../../../../api/branches/api'
import { useVehicleClassesQuery } from '../../../../api/vehicle-class/api'
import { VEHICLE_ENGINES } from '../../../../constants/constants'
import { useUser } from '../../../../user-provider/user-context'

export const Route = createFileRoute('/app/settings/branch-settings/service-capabilities')({
  component: ServiceCapabilitiesComponent,
})

const serviceCapabilitiesSchema = z.object({
  labor_rate: z.coerce.number().positive().max(199.99),
  vehicle_class_ids: z.array(z.string().uuid()),
  vehicle_engines: z.array(z.enum(VEHICLE_ENGINES)),
})

type ServiceCapabilitiesFormData = z.infer<typeof serviceCapabilitiesSchema>

function ServiceCapabilitiesComponent() {
  const { branchInfo } = useUser()
  const branchId = branchInfo?.provider_branch_id || ''
  const { data: branch, isLoading: branchLoading } = useBranchQuery(branchId)
  const { data: branchVehicleClasses, isLoading: branchVehicleClassesLoading } = useBranchVehicleClassesQuery(branchId)
  const { data: vehicleClasses, isLoading: vehicleClassesLoading } = useVehicleClassesQuery()
  const updateBranchMutation = useUpdateBranchMutation()

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<ServiceCapabilitiesFormData>({
    resolver: zodResolver(serviceCapabilitiesSchema),
    defaultValues: {
      labor_rate: 0,
      vehicle_class_ids: [],
      vehicle_engines: [],
    },
  })

  useEffect(() => {
    if (branch && branchVehicleClasses) {
      // NOTE: using reset because setValue sets the form to dirty
      reset({
        labor_rate: branch.labor_rate || 0,
        vehicle_engines: branch.vehicle_engines || [],
        vehicle_class_ids: branchVehicleClasses.map((vc) => vc.id),
      })
    }
  }, [branchVehicleClasses, branch, reset])

  const onSubmit = (serviceData: ServiceCapabilitiesFormData) => {
    if (branchId) {
      updateBranchMutation.mutate({
        branchId,
        data: {
          labor_rate: serviceData.labor_rate,
          vehicle_class_ids: serviceData.vehicle_class_ids,
          vehicle_engines: serviceData.vehicle_engines,
        },
      })
    }
  }

  const vehicleClassOptions =
    vehicleClasses?.items.map((vc) => ({
      value: vc.id,
      label: vc.name,
    })) || []

  const vehicleEngineOptions = VEHICLE_ENGINES.map((engine) => ({
    value: engine,
    label: engine,
  }))

  return (
    <Card className="service-capabilities-content">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={0}>
          <Controller
            name="vehicle_class_ids"
            control={control}
            render={({ field }) => (
              <MultiSelect
                {...field}
                label="Supported vehicle classes"
                placeholder="Select vehicle classes your shop supports"
                data={vehicleClassOptions}
                searchable
                description="Up to which vehicle class is your shop able to service?"
                disabled={branchLoading || branchVehicleClassesLoading || vehicleClassesLoading}
                error={errors.vehicle_class_ids?.message}
              />
            )}
          />

          <Controller
            name="vehicle_engines"
            control={control}
            render={({ field }) => (
              <MultiSelect
                {...field}
                label="Supported vehicle engines"
                placeholder="Select engine types your shop supports"
                data={vehicleEngineOptions}
                description="Which vehicle engines is your shop able to service?"
                disabled={branchLoading}
                error={errors.vehicle_engines?.message}
              />
            )}
          />

          <Controller
            name="labor_rate"
            control={control}
            render={({ field }) => (
              <NumberInput
                {...field}
                label="Labor Rate"
                placeholder="Enter labor rate"
                min={0}
                step={0.01}
                decimalScale={2}
                prefix="$"
                description="Provide your most competitive shop labor rate for large, commercial fleet customers. Pricing competitively significantly increases your chances of winning requests. Your work orders will be evaluated for labor times based on the labor rate you provide here. Any changes to your labor rate will only apply to new offers that are accepted."
                disabled={branchLoading}
                error={errors.labor_rate?.message}
              />
            )}
          />

          <Group style={{ marginTop: 24 }} justify="flex-end">
            <Button
              type="submit"
              loading={updateBranchMutation.isPending}
              disabled={!isDirty || branchLoading || branchVehicleClassesLoading || vehicleClassesLoading}
            >
              Save changes
            </Button>
          </Group>
        </Stack>
      </form>
    </Card>
  )
}
